import * as yup from "yup";

import type {
  CreateForeignNationalRequest,
  ForeignNational
} from "../../api/types/foreignNational";
import { newClientFormSchema } from "../../forms/client/Client.schema";
import { isPetitionerVisa, VISAVALUE } from "../../types/visa.type";
import {
  EMPTY_CLIENT_DETAILS,
  toBirthdayPayload
} from "../foreignNationals/components/ClientDetailsFields";
import {
  isNoticeWorkflow,
  noticeWorkflowLabel,
  type CreateCaseValues
} from "../clientProfile/tabs/cases/useCreateCaseForm";
import type { Company } from "../companies/types";
import { customTypeIdOf } from "../customCaseTypes/customCaseTypes";
import type { NoticePrefill } from "../keyFacts/types";
import {
  NOIR_NOID_WORKFLOW_OPTION,
  RFE_WORKFLOW_OPTION
} from "../../types/workflow.type";
import type {
  NewCaseStep,
  NewCaseValues,
  PetitionerCompany,
  PetitionerKind,
  SelectedClient
} from "./types";

export const INITIAL_NEW_CASE_VALUES: NewCaseValues = {
  ...EMPTY_CLIENT_DETAILS,
  mode: null,
  client: null,
  workflow: "",
  noticeVisa: "",
  petitioner: "",
  petitionerCompany: null,
  petitionerKind: "company",
  filingDeadline: "",
  assignedUserIds: [],
  noticeFile: null,
  noticePreview: null,
  createdClient: null
};

/** Where an unfinished wizard's answers are kept for the tab (`sessionDraft`). */
export const newCaseDraftKey = (currentUserId: string) =>
  `new-case:${currentUserId.trim() || "anon"}`;

/** Answers that can be kept as text: everything but files and reads in flight. */
const KEPT_KEYS = [
  ...(Object.keys(EMPTY_CLIENT_DETAILS) as Array<keyof NewCaseValues>),
  "mode",
  "client",
  "workflow",
  "noticeVisa",
  "petitioner",
  "petitionerCompany",
  "filingDeadline",
  "assignedUserIds",
  "createdClient"
] as const;

/** The answers worth keeping, or null when nothing has been answered. */
export const encodeNewCaseDraft = (values: NewCaseValues): string | null => {
  if (!values.mode && !values.workflow) return null;
  const kept: Record<string, unknown> = {};
  for (const key of KEPT_KEYS) kept[key] = values[key];
  return JSON.stringify(kept);
};

/** Kept answers over `base`; anything unreadable is ignored. */
export const decodeNewCaseDraft = (
  raw: string | null,
  base: NewCaseValues
): NewCaseValues => {
  if (!raw) return base;
  try {
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== "object" || parsed === null) return base;
    const out = { ...base } as Record<string, unknown>;
    for (const key of KEPT_KEYS) {
      const value = (parsed as Record<string, unknown>)[key];
      const expected = base[key];
      if (
        value !== undefined &&
        (expected === null || typeof value === typeof expected)
      )
        out[key] = value;
    }
    return out as NewCaseValues;
  } catch {
    return base;
  }
};

/** A new case's answers, with the person creating it on the team. */
export const initialNewCaseValues = (currentUserId: string): NewCaseValues => ({
  ...INITIAL_NEW_CASE_VALUES,
  assignedUserIds: currentUserId.trim() ? [currentUserId.trim()] : []
});

/** The `/clients/new` rules without the assignee list. */
export const clientDetailsSchema = newClientFormSchema.omit([
  "assignedUserIds"
]);

export const isClientDetailsValid = (values: NewCaseValues): boolean =>
  clientDetailsSchema.isValidSync(values);

/** The underlying visa: the original filing's for a notice, else the workflow. */
export const baseVisaOf = (values: NewCaseValues): string =>
  isNoticeWorkflow(values.workflow)
    ? values.noticeVisa
    : customTypeIdOf(values.workflow)
    ? VISAVALUE.CUSTOM_TEMPLATE
    : values.workflow;

/** Employment petitions the beneficiary usually files for themselves. */
const SELF_PETITION_VISAS: ReadonlySet<string> = new Set([
  VISAVALUE.EB1A,
  VISAVALUE.EB2NIW,
  VISAVALUE.EB2NIW_V2
]);

/**
 * Filings with no petitioner but the beneficiary: the taxonomy's
 * `self_petition: "always"` visas.
 */
const SELF_FILED_VISAS: ReadonlySet<string> = new Set([
  VISAVALUE.EB5,
  VISAVALUE.I829,
  VISAVALUE.VAWA
]);

/**
 * Whether the wizard asks who the petitioner is: not for a filing only the
 * beneficiary makes (EB-5), nor for an EB-1A or NIW whose notice shows the
 * beneficiary filed it themselves.
 */
export const asksPetitioner = (values: NewCaseValues): boolean => {
  const visa = baseVisaOf(values);
  if (SELF_FILED_VISAS.has(visa)) return false;
  const preview = values.noticePreview;
  const selfFiledNotice =
    isNoticeWorkflow(values.workflow) &&
    preview?.status === "ready" &&
    preview.prefill?.self_petition === true;
  return !(selfFiledNotice && SELF_PETITION_VISAS.has(visa));
};

/** The steps the wizard walks for these answers, in order. */
export const stepsFor = (values: NewCaseValues): NewCaseStep[] => [
  "clientMode",
  "client",
  "workflow",
  ...(isNoticeWorkflow(values.workflow)
    ? (["notice", "noticeVisa"] as const)
    : []),
  ...(asksPetitioner(values) ? (["petitioner"] as const) : []),
  "details"
];

export const canAdvance = (step: NewCaseStep, values: NewCaseValues) => {
  switch (step) {
    case "clientMode":
      return values.mode !== null;
    case "client":
      if (values.mode === "existing") return values.client !== null;
      if (values.mode === "new") return isClientDetailsValid(values);
      return false;
    case "workflow":
      return Boolean(values.workflow);
    case "notice":
      return true;
    case "noticeVisa":
      return Boolean(values.noticeVisa);
    case "petitioner":
    case "details":
      return true;
    default:
      return false;
  }
};

/**
 * Where the petitioner name lives on the case: petitioner visas (family
 * petitions, waivers, H-2/H-3) name a person, everything else an employer.
 */
export const petitionerFieldFor = (
  visa: string
): "petitionerName" | "employerInfo" =>
  isPetitionerVisa(visa as VISAVALUE) ? "petitionerName" : "employerInfo";

/** The petitioner question's helper line for this visa. */
export const petitionerHint = (visa: string): string => {
  if (isPetitionerVisa(visa as VISAVALUE)) {
    return "Optional. Leave blank if the client is self-petitioning.";
  }
  return SELF_PETITION_VISAS.has(visa)
    ? "Optional. Leave blank for a self-petition, or pick the sponsoring employer."
    : "Optional. Pick the firm's company, or leave blank if there is no petitioner.";
};

/** The individual update that records the petitioner, or null when blank. */
export const petitionerUpdate = (
  visa: string,
  petitioner: string,
  kind: PetitionerKind = "company"
): Record<string, unknown> | null => {
  const name = petitioner.trim();
  if (!name) return null;
  return petitionerFieldFor(visa) === "petitionerName" || kind === "person"
    ? { petitionerName: name }
    : { employerInfo: { legalBusinessName: name } };
};

const COMPANY_WORDS =
  /\b(llc|l\.l\.c|inc|incorporated|corp|corporation|co|company|ltd|limited|lp|llp|plc|gmbh|group|holdings|partners|university|college|institute|hospital|center|centre|foundation|labs?|technologies|systems|solutions|ventures|enterprises|services)\b\.?/i;

/** Whether a petitioner name reads as an organization rather than a person. */
export const looksLikeCompany = (name: string): boolean =>
  COMPANY_WORDS.test(name);

/** A company petitioner applies only where the petitioner is an employer. */
export const companyPetitionerFor = (
  visa: string,
  values: Pick<NewCaseValues, "petitionerCompany">
): PetitionerCompany | null =>
  petitionerFieldFor(visa) === "employerInfo" ? values.petitionerCompany : null;

const normalName = (name: string) =>
  name
    .toLowerCase()
    .replace(/[^a-z0-9 ]/g, " ")
    .replace(/\s+/g, " ")
    .trim();

/** Legal-form words a notice adds and a firm's company name often drops. */
const LEGAL_FORMS = new Set([
  "inc",
  "incorporated",
  "corp",
  "corporation",
  "co",
  "company",
  "llc",
  "llp",
  "lp",
  "ltd",
  "limited",
  "plc",
  "pc",
  "pllc",
  "the"
]);

/** A company name without its legal form: "qa helix biosciences". */
const coreName = (name: string) =>
  normalName(name)
    .split(" ")
    .filter((word) => !LEGAL_FORMS.has(word))
    .join(" ");

/**
 * Firm companies that a name names: the query is in the company's name or an
 * entity's legal name, or (legal forms aside) the name is the company's, so
 * "QA-HELIX BIOSCIENCES CORP." finds "QA-Helix Biosciences".
 */
export const matchCompanies = <T extends Pick<Company, "name" | "entities">>(
  companies: T[],
  query: string,
  limit = 6
): T[] => {
  const q = normalName(query);
  if (!q) return companies.slice(0, limit);
  const core = coreName(query);
  return companies
    .filter((c) =>
      [c.name, ...c.entities.map((e) => e.legal_name)].some((n) => {
        const name = coreName(n);
        return (
          normalName(n).includes(q) ||
          (core.length >= 3 && name.length >= 3 && name === core)
        );
      })
    )
    .slice(0, limit);
};

/** Acronyms kept in capitals when a name in capitals is set in title case. */
const KEPT_CAPITALS = new Set([
  "LLC",
  "LLP",
  "LP",
  "PLC",
  "PC",
  "PLLC",
  "USA",
  "US"
]);

/**
 * A name as printed on a USCIS notice, in capitals, set in title case:
 * "QA-HELIX BIOSCIENCES CORP." becomes "QA-Helix Biosciences Corp.". Names
 * with any lower case are left as written; two-letter parts stay capitals.
 */
export const fromNoticeCase = (name: string): string => {
  const trimmed = name.trim();
  if (!/[A-Z]/.test(trimmed) || trimmed !== trimmed.toUpperCase())
    return trimmed;
  return trimmed.replace(/[A-Z][A-Z']*/g, (word) =>
    word.length <= 2 || KEPT_CAPITALS.has(word)
      ? word
      : word[0] + word.slice(1).toLowerCase()
  );
};

export const toCreateCaseValues = (
  values: NewCaseValues
): CreateCaseValues => ({
  workflow: values.workflow || null,
  noticeVisa: values.noticeVisa || null,
  filingDeadline: values.filingDeadline,
  assignedUserIds: values.assignedUserIds,
  customName: "",
  saveForFirm: false
});

export const toCreateClientPayload = (
  values: NewCaseValues,
  currentUserId: string
): CreateForeignNationalRequest => ({
  first_name: values.firstName.trim(),
  middle_name: values.middleName.trim() || undefined,
  last_name: values.lastName.trim(),
  email: values.email.trim(),
  birthday: toBirthdayPayload(values.birthday),
  phone: values.phone.trim() || undefined,
  title: values.title.trim() || undefined,
  pronouns: values.pronouns.trim() || undefined,
  assigned_to_user_ids: currentUserId.trim()
    ? [currentUserId.trim()]
    : undefined
});

export const isEmailExistsError = (message: string): boolean =>
  /EMAIL_EXISTS|already exists/i.test(message);

export const toSelectedClient = (fn: ForeignNational): SelectedClient => ({
  id: fn.uid,
  name: [fn.first_name, fn.middle_name, fn.last_name]
    .map((part) => (part ?? "").trim())
    .filter(Boolean)
    .join(" "),
  email: (fn.email ?? "").trim()
});

export const clientInitials = (client: SelectedClient): string => {
  const parts = client.name.split(/\s+/).filter(Boolean);
  const initials = parts.length
    ? `${parts[0][0]}${parts.length > 1 ? parts[parts.length - 1][0] : ""}`
    : client.email.slice(0, 1);
  return initials.toUpperCase() || "?";
};

export const validateNewCaseValues = async (
  values: NewCaseValues
): Promise<Record<string, string>> => {
  if (values.mode !== "new") return {};
  try {
    await clientDetailsSchema.validate(values, { abortEarly: false });
    return {};
  } catch (err) {
    if (!(err instanceof yup.ValidationError)) return {};
    return Object.fromEntries(
      err.inner
        .filter((issue) => issue.path)
        .map((issue) => [issue.path as string, issue.message])
    );
  }
};

/** Workflows with no RFE or NOID/NOIR of their own: never a notice's base filing. */
export const NO_NOTICE_WORKFLOWS: string[] = [
  VISAVALUE.PERM,
  VISAVALUE.BOND,
  VISAVALUE.HABEAS,
  VISAVALUE.MANDAMUS,
  VISAVALUE.I246,
  VISAVALUE.VAWA_CANCELLATION
];

const VISA_VALUES: ReadonlySet<string> = new Set(Object.values(VISAVALUE));

/** A notice's read visa as a base filing the wizard offers, else "". */
export const noticeVisaFrom = (visa: string | null | undefined): string =>
  visa && VISA_VALUES.has(visa) && !NO_NOTICE_WORKFLOWS.includes(visa)
    ? visa
    : "";

/** The wizard workflow option a notice answers, from its read type. */
export const workflowOptionFor = (prefill: NoticePrefill): string | null => {
  if (prefill.workflow === "rfe") return RFE_WORKFLOW_OPTION;
  if (prefill.workflow === "noir_noid") return NOIR_NOID_WORKFLOW_OPTION;
  return null;
};

type NoticeFilled = Partial<
  Pick<NewCaseValues, "noticeVisa" | "filingDeadline" | "petitioner">
>;

const NOTICE_FILLED_KEYS = [
  "noticeVisa",
  "filingDeadline",
  "petitioner"
] as const;

/**
 * What a read notice fills in: the workflow it answers, its base filing, the
 * response deadline and the petitioner. Only empty answers are
 * filled, except the workflow, which follows the notice. `filled` is what an
 * earlier notice filled in; answers still equal to it are the notice's, not
 * the attorney's, so a replacement notice overwrites them.
 */
export const noticePrefillUpdates = (
  values: NewCaseValues,
  prefill: NoticePrefill,
  filled: NoticeFilled = {}
): Partial<NewCaseValues> => {
  const open = (key: keyof NoticeFilled) =>
    !values[key].trim() || values[key] === filled[key];
  const updates: Partial<NewCaseValues> = {};
  const workflow = workflowOptionFor(prefill);
  if (workflow && workflow !== values.workflow) updates.workflow = workflow;
  const visa = noticeVisaFrom(prefill.visa);
  if (open("noticeVisa") && visa) updates.noticeVisa = visa;
  if (open("filingDeadline") && prefill.response_deadline)
    updates.filingDeadline = prefill.response_deadline;
  const petitioner = (prefill.petitioner_name ?? "").trim();
  const beneficiary = (prefill.beneficiary_name ?? "").trim();
  if (
    open("petitioner") &&
    !values.petitionerCompany &&
    !prefill.self_petition &&
    petitioner &&
    petitioner.toLowerCase() !== beneficiary.toLowerCase()
  ) {
    updates.petitioner = fromNoticeCase(petitioner);
    updates.petitionerKind = looksLikeCompany(petitioner)
      ? "company"
      : "person";
  }
  for (const key of NOTICE_FILLED_KEYS)
    if (!(key in updates) && filled[key] && values[key] === filled[key])
      updates[key] = "";
  return updates;
};

/** The answers a removed notice filled in and the attorney left alone, cleared. */
export const noticeFilledResets = (
  values: NewCaseValues,
  filled: NoticeFilled
): Partial<NewCaseValues> => {
  const resets: Partial<NewCaseValues> = {};
  for (const key of NOTICE_FILLED_KEYS)
    if (filled[key] && values[key] === filled[key]) resets[key] = "";
  return resets;
};

/** The part of a prefill update the notice owns (everything but the workflow). */
export const noticeFilledFrom = (
  updates: Partial<NewCaseValues>
): NoticeFilled => {
  const filled: NoticeFilled = {};
  for (const key of NOTICE_FILLED_KEYS)
    if (updates[key]) filled[key] = updates[key];
  return filled;
};

const NOTICE_LABELS: Record<NoticePrefill["notice_type"], string> = {
  rfe: "RFE",
  noid: "NOID",
  noir: "NOIR",
  denial: "Denial",
  other: "Notice"
};

/** "NOID" once the notice is read as one; the workflow's label before that. */
export const noticeLabelOf = (values: NewCaseValues): string => {
  const preview = values.noticePreview;
  const read =
    preview?.status === "ready" ? preview.prefill?.notice_type : undefined;
  return read === "rfe" || read === "noid" || read === "noir"
    ? NOTICE_LABELS[read]
    : noticeWorkflowLabel(values.workflow);
};

const dayOf = (iso: string | null): string | null => {
  if (!iso) return null;
  const [y, m, d] = iso.split("-").map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric"
  });
};

/** `NOID · LIN2390000001 · Issued Oct 1, 2026 · Response due Nov 3, 2026 · 2 issues`. */
export const noticeSummary = (prefill: NoticePrefill): string[] => {
  const issued = dayOf(prefill.issue_date);
  const due = dayOf(prefill.response_deadline);
  return [
    NOTICE_LABELS[prefill.notice_type] ?? "Notice",
    prefill.receipt_number ?? "",
    issued ? `Issued ${issued}` : "",
    due
      ? `Response due ${due}${prefill.deadline_computed ? " (computed)" : ""}`
      : "",
    `${prefill.issue_count} issue${prefill.issue_count === 1 ? "" : "s"}`
  ].filter(Boolean);
};
